import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { pdfColors } from "../colors";
import { EnvironmentalData } from "@/services/environmentalDataService";
import { PDFColors } from "../types";

export const addEndangeredSpeciesSection = (
  doc: jsPDF,
  data: EnvironmentalData,
  startY: number,
  colors: PDFColors,
): void => {
  let currentY = startY;

  // Rule 1: H1 Style (Bold, Uppercase)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
  doc.text("ENDANGERED SPECIES & BIODIVERSITY", 14, currentY);
  currentY += 10;

  // Rule 3: Key Indicators Block
  autoTable(doc, {
    startY: currentY,
    body: [
      [
        { content: "Total Species:", styles: { fontStyle: "bold" } },
        data.endangeredSpecies.count.toString(),
        { content: "Critically Endangered:", styles: { fontStyle: "bold" } },
        data.endangeredSpecies.criticallyEndangered.toString(),
        { content: "Endangered:", styles: { fontStyle: "bold" } },
        data.endangeredSpecies.endangered.toString(),
        { content: "Vulnerable:", styles: { fontStyle: "bold" } },
        data.endangeredSpecies.vulnerable.toString(),
      ],
    ],
    theme: "grid",
    styles: {
      fontSize: 9,
      cellPadding: 4,
      fillColor: [245, 247, 249] as [number, number, number],
    },
    margin: { left: 14, right: 14 },
  });

  currentY = (doc as any).lastAutoTable.finalY + 12;

  // Rule 2: H2
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
  doc.text("Top Threatened Species Analysis", 14, currentY);
  currentY += 8;

  // Rule 7: Species Table
  autoTable(doc, {
    head: [["Species", "Scientific Name", "Status", "Population", "Trend"]],
    body: data.endangeredSpecies.topThreatenedSpecies.map((species) => [
      species.name,
      species.scientificName,
      species.status,
      typeof species.population === "number"
        ? species.population.toLocaleString()
        : species.population,
      species.trend,
    ]),
    startY: currentY,
    theme: "striped",
    headStyles: {
      fillColor: colors.primary,
      textColor: [255, 255, 255] as [number, number, number],
    },
    styles: { fontSize: 10 },
    margin: { left: 14, right: 14 },
    columnStyles: {
      2: {
        cellWidth: "auto",
        fontStyle: "bold",
        textColor: function (data) {
          const status = data.cell.raw;
          if (status === "Critically Endangered")
            return colors.danger as [number, number, number];
          if (status === "Endangered")
            return colors.warning as [number, number, number];
          return colors.text as [number, number, number];
        } as any,
      },
      4: {
        textColor: function (data) {
          const trend = data.cell.raw;
          if (trend === "Decreasing")
            return colors.danger as [number, number, number];
          if (trend === "Increasing")
            return colors.success as [number, number, number];
          return colors.text as [number, number, number];
        } as any,
      },
    },
  });

  currentY = (doc as any).lastAutoTable.finalY + 10;

  // Rule 7: Short interpretive sentence below the table
  doc.setFont("helvetica", "italic");
  doc.setFontSize(10);
  doc.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
  const summaryText = `Analysis indicates that ${data.endangeredSpecies.criticallyEndangered} species are at immediate risk of extinction in this region, with a predominantly ${data.endangeredSpecies.topThreatenedSpecies.some((s) => s.trend === "Decreasing") ? "decreasing" : "stable"} population trend observed among key indicator species.`;
  const splitTitle = doc.splitTextToSize(
    summaryText,
    doc.internal.pageSize.getWidth() - 28,
  );
  doc.text(splitTitle, 14, currentY);
};
